import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { KeyboardShortcutsProvider } from './contexts/KeyboardShortcutsContext';
import Dashboard from './components/Dashboard';
import Chat from './components/Chat';
import Login from './components/Login';
import './styles/theme.css';
import './App.css';

// Create MUI theme based on dark mode state
const createAppTheme = (isDarkMode: boolean) => createTheme({
  palette: {
    mode: isDarkMode ? 'dark' : 'light',
    primary: {
      main: isDarkMode ? '#60a5fa' : '#2563eb',
      light: isDarkMode ? '#93c5fd' : '#3b82f6',
      dark: isDarkMode ? '#3b82f6' : '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: isDarkMode ? '#94a3b8' : '#64748b',
      light: isDarkMode ? '#cbd5e1' : '#94a3b8',
      dark: isDarkMode ? '#64748b' : '#475569',
    },
    background: {
      default: isDarkMode ? '#0f172a' : '#f8fafc',
      paper: isDarkMode ? '#1e293b' : '#ffffff',
    },
    text: {
      primary: isDarkMode ? '#f8fafc' : '#0f172a',
      secondary: isDarkMode ? '#cbd5e1' : '#475569',
    },
    error: {
      main: '#ef4444',
      light: '#fca5a5',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fcd34d',
      dark: '#d97706',
    },
    success: {
      main: '#22c55e',
      light: '#86efac',
      dark: '#16a34a',
    },
    info: {
      main: '#0ea5e9',
      light: '#7dd3fc',
      dark: '#0284c7',
    },
    divider: isDarkMode ? '#334155' : '#e2e8f0',
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 500, textTransform: 'none' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          backgroundImage: 'none',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: isDarkMode ? '#1e293b' : '#f8fafc',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

// Inner app component that has access to theme
const AppContent: React.FC = () => {
  const { isDarkMode } = useTheme();
  const muiTheme = useMemo(() => createAppTheme(isDarkMode), [isDarkMode]);
  
  // Determine basename based on environment
  const hostname = window.location.hostname;
  const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
  const isNgrok = hostname.includes('ngrok.io') || 
                  hostname.includes('ngrok-free.app') || 
                  hostname.includes('ngrok-free.dev') ||
                  hostname.includes('ngrok.app') ||
                  hostname.includes('ngrok.dev');
  const isSubdomain = hostname.includes('support.bonyad-hub.com') || hostname.includes('support.');
  const basename = (isDevelopment || isNgrok || isSubdomain) ? undefined : '/support';
  
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Router basename detection:', {
      hostname,
      isDevelopment,
      isNgrok,
      isSubdomain,
      basename: basename || 'undefined (root)'
    });
  }
  
  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <AuthProvider>
        <Router basename={basename}>
          <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/chat/:id"
                element={
                  <PrivateRoute>
                    <Chat />
                  </PrivateRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </MuiThemeProvider>
  );
};

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

function App() {
  return (
    <ThemeProvider>
      <KeyboardShortcutsProvider>
        <AppContent />
      </KeyboardShortcutsProvider>
    </ThemeProvider>
  );
}

export default App;
